export class ApiError extends Error {
  constructor(message, { status, payload, retryAfter } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
    this.code = payload?.code;
    this.limit = payload?.limit;
    this.retryAfter = payload?.retry_after || responseRetryAfter(payload) || (retryAfter == null ? null : Number(retryAfter) || null);
  }
}

function responseRetryAfter(payload) {
  const value = payload?.retryAfter;
  return value == null || value === "" ? null : Number(value) || null;
}

export function apiPrefix(pathname = window.location.pathname) {
  const match = String(pathname || "/").match(
    /^(.*?)(?:\/app(?:\.html)?(?:\/[^/?#]*)*|\/studio(?:\.html)?(?:\/[^/?#]*)*|\/[a-z]+\.html)$/,
  );
  if (!match) return "";
  return match[1] === "/" ? "" : match[1];
}

export function assetUrl(path) {
  const prefix = globalThis.SOA_CONFIG?.assetPrefix ?? apiPrefix();
  return `${prefix}${path.startsWith("/") ? path : `/${path}`}`;
}

let unauthorizedHandler;

export function onUnauthorized(handler) {
  unauthorizedHandler = handler;
}

function errorMessage(response, payload) {
  if (payload && typeof payload === "object") {
    // `msg` first: it is the field the server writes FOR THE PERSON (the paywall sentence, the
    // capacity explanation). `detail`/`message` are often a framework's or a service's own words
    // and `error` is a code — all three are worse to show when a written sentence is present.
    const detail = payload.msg || payload.detail || payload.message || payload.error;
    if (typeof detail === "string" && detail) return detail;
  }
  return `Request failed with status ${response.status}`;
}

async function responsePayload(response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function api(path, options = {}) {
  const { json, headers, ...requestOptions } = options;
  const requestHeaders = new Headers(headers);
  requestHeaders.set("Accept", "application/json");

  if (json !== undefined) {
    requestHeaders.set("Content-Type", "application/json");
    requestOptions.body = JSON.stringify(json);
  }

  const response = await fetch(`${apiPrefix()}${path}`, {
    credentials: "same-origin",
    ...requestOptions,
    headers: requestHeaders,
  });
  const payload = await responsePayload(response);

  if (!response.ok) {
    if (response.status === 401 && unauthorizedHandler) unauthorizedHandler();
    throw new ApiError(errorMessage(response, payload), {
      status: response.status,
      payload,
      retryAfter: response.headers.get("retry-after"),
    });
  }
  return payload;
}
